/**
 * LeadsTab — sales pipeline for enquiries captured via the public
 * contact form. Powered by /api/admin/leads (backed by marketing_alerts).
 *
 * Design goals:
 *   • KPI row at the top so the founder sees pipeline at a glance
 *   • Fast filtering (status, sector, priority, free-text search, date)
 *   • Row click → slide-out drawer with everything about the lead:
 *       full message, editable status/priority/value/owner/next-action,
 *       notes thread, change history.
 *   • CSV export honours the current filter.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Inbox, Search, RefreshCw, FileDown, X, ExternalLink, Save,
  MessageCircle, Clock, Tag, Trash2, TrendingUp, CheckCircle2,
} from 'lucide-react';
import { BACKEND_URL } from '../../lib/api';

const API = BACKEND_URL;
const TOKEN_KEY = 'admin_console_token';
const getToken = () => localStorage.getItem(TOKEN_KEY);
const authHead = () => ({ Authorization: `Bearer ${getToken()}` });

// Colour palette — matches AdminConsole's `P` object semantically.
const P = {
  bg: '#F7F8FB', surface: '#FFFFFF', ink: '#0F172A', slate: '#475569',
  muted: '#94A3B8', border: '#E5E7EB', borderSoft: '#EEF0F4',
  primary: '#0A2540', primarySoft: '#EEF2F7', accent: '#4F46E5',
  accentSoft: '#EEF2FF', danger: '#DC2626', dangerSoft: '#FEF2F2',
  success: '#059669', successSoft: '#ECFDF5',
  warn: '#D97706', warnSoft: '#FFFBEB',
  info: '#0369A1', infoSoft: '#F0F9FF',
  pink: '#DB2777', pinkSoft: '#FDF2F8',
};

const STATUS_META = {
  new:              { label: 'New',             bg: P.infoSoft,    fg: P.info },
  approached:       { label: 'Approached',      bg: P.warnSoft,    fg: P.warn },
  in_conversation:  { label: 'In conversation', bg: P.accentSoft,  fg: P.accent },
  qualified:        { label: 'Qualified',       bg: P.pinkSoft,    fg: P.pink },
  converted:        { label: 'Converted',       bg: P.successSoft, fg: P.success },
  lost:             { label: 'Lost',            bg: P.dangerSoft,  fg: P.danger },
  archived:         { label: 'Archived',        bg: '#F1F5F9',     fg: P.slate },
};

const PRIORITY_META = {
  low:    { label: 'Low',    fg: P.slate },
  normal: { label: 'Normal', fg: P.info },
  high:   { label: 'High',   fg: P.danger },
};

const CCY_SYMBOL = { GBP: '£', EUR: '€', USD: '$' };

const SECTORS = [
  'Dairy & cheese', 'Bakery', 'Meat processing', 'Seafood & fish',
  'Prepared food & ready meals', 'Sauces, preserves & ambient',
  'Beverages', 'Wholesale & distribution', 'Other',
];

// ─────────────────────────────────────────────────────────────────────
export default function LeadsTab({ onUnauth }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);   // lead being viewed in drawer
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  // Filters
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [sector, setSector] = useState('');
  const [priority, setPriority] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (q) p.q = q;
    if (status) p.status = status;
    if (sector) p.sector = sector;
    if (priority) p.priority = priority;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    return p;
  }, [page, pageSize, q, status, sector, priority, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/leads`, { params, headers: authHead() }),
        axios.get(`${API}/api/admin/leads/stats`, { headers: authHead() }),
      ]);
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) { onUnauth?.(); return; }
      setErr(e?.response?.data?.detail || e.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [params, onUnauth]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const patchLead = async (id, patch) => {
    const { data } = await axios.patch(`${API}/api/admin/leads/${id}`, patch, { headers: authHead() });
    // Refresh the row inline + drawer
    setItems(prev => prev.map(x => x.id === id ? data : x));
    if (selected?.id === id) setSelected(data);
    // Stats can shift when status changes; refresh stats lightly
    axios.get(`${API}/api/admin/leads/stats`, { headers: authHead() })
      .then(r => setStats(r.data)).catch(() => {});
    return data;
  };

  const addNote = async (id, text) => {
    const { data } = await axios.post(`${API}/api/admin/leads/${id}/notes`,
      { text }, { headers: authHead() });
    setItems(prev => prev.map(x => x.id === id ? data : x));
    if (selected?.id === id) setSelected(data);
    return data;
  };

  const archiveLead = async (id) => {
    if (!window.confirm('Archive this lead? It will be hidden from the default view.')) return;
    await axios.delete(`${API}/api/admin/leads/${id}`, { headers: authHead() });
    setSelected(null);
    setRefreshTick(t => t + 1);
  };

  const exportCsv = () => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined && k !== 'page' && k !== 'page_size') usp.append(k, v);
    });
    // Fetch as blob so we can force download without opening a new tab
    axios.get(`${API}/api/admin/leads/export.csv?${usp.toString()}`, {
      headers: authHead(), responseType: 'blob',
    }).then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `keystone-leads-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    }).catch(() => setErr('CSV export failed'));
  };

  const clearFilters = () => {
    setQ(''); setStatus(''); setSector(''); setPriority('');
    setDateFrom(''); setDateTo(''); setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div data-testid="leads-tab">
      <TabHeader
        onRefresh={() => setRefreshTick(t => t + 1)}
        onExport={exportCsv}
        loading={loading}
      />

      <KpiRow stats={stats} />

      <FilterBar
        q={q} setQ={setQ}
        status={status} setStatus={setStatus}
        sector={sector} setSector={setSector}
        priority={priority} setPriority={setPriority}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        onClear={clearFilters}
      />

      {err && (
        <div data-testid="leads-error"
          style={{ background: P.dangerSoft, color: P.danger, padding: '10px 14px',
                   borderRadius: 8, fontSize: 13, marginTop: 12, border: `1px solid ${P.danger}22` }}>
          {err}
        </div>
      )}

      <LeadsTable
        items={items}
        loading={loading}
        onRowClick={setSelected}
        onStatusChange={(id, s) => patchLead(id, { status: s })}
      />

      <Pagination
        page={page} totalPages={totalPages} total={total}
        pageSize={pageSize} onPage={setPage}
      />

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onSave={patchLead}
          onAddNote={addNote}
          onArchive={archiveLead}
        />
      )}
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────
function TabHeader({ onRefresh, onExport, loading }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                      fontWeight: 700, color: P.muted, marginBottom: 6 }}>
          Admin CMS · Sales pipeline
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: P.ink, margin: 0, letterSpacing: '-0.01em' }}>
          Leads
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: 13.5, color: P.slate, maxWidth: 640 }}>
          Every enquiry from the marketing site — captured, actionable, exportable.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button data-testid="leads-refresh-btn" onClick={onRefresh}
          style={btnGhost}>
          <RefreshCw size={14} style={{ opacity: loading ? 0.4 : 1 }}/> Refresh
        </button>
        <button data-testid="leads-export-btn" onClick={onExport} style={btnPrimary}>
          <FileDown size={14} /> Export CSV
        </button>
      </div>
    </div>
  );
}

// ─── KPI Row ─────────────────────────────────────────────────────────
function KpiRow({ stats }) {
  const c = stats?.counts || {};
  const pv = stats?.pipeline_value || {};
  const cards = [
    { key: 'new',              label: 'New',              value: c.new || 0,              tone: 'info' },
    { key: 'approached',       label: 'Approached',       value: c.approached || 0,       tone: 'warn' },
    { key: 'in_conversation',  label: 'In conversation',  value: c.in_conversation || 0,  tone: 'accent' },
    { key: 'qualified',        label: 'Qualified',        value: c.qualified || 0,        tone: 'pink' },
    { key: 'converted_30d',    label: 'Converted (30d)',  value: c.converted_30d || 0,    tone: 'success' },
  ];
  const tones = {
    info: { bg: P.infoSoft, fg: P.info },
    warn: { bg: P.warnSoft, fg: P.warn },
    accent: { bg: P.accentSoft, fg: P.accent },
    pink: { bg: P.pinkSoft, fg: P.pink },
    success: { bg: P.successSoft, fg: P.success },
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 12, marginBottom: 20 }}
         data-testid="leads-kpi-row">
      {cards.map(card => (
        <div key={card.key} style={{
          background: P.surface, border: `1px solid ${P.border}`,
          borderRadius: 10, padding: '14px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: 4, background: tones[card.tone].fg,
            }}/>
            <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                           fontWeight: 700, color: P.muted }}>{card.label}</span>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: P.ink,
                        fontVariantNumeric: 'tabular-nums' }}
               data-testid={`kpi-${card.key}`}>
            {card.value}
          </div>
        </div>
      ))}
      {/* Pipeline value totals */}
      <div style={{
        background: P.surface, border: `1px solid ${P.border}`,
        borderRadius: 10, padding: '14px 16px',
        gridColumn: 'span 1',
      }} data-testid="kpi-pipeline-value">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <TrendingUp size={13} color={P.primary}/>
          <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                         fontWeight: 700, color: P.muted }}>Pipeline value</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontVariantNumeric: 'tabular-nums' }}>
          {['GBP', 'EUR', 'USD'].map(ccy => {
            const v = pv[ccy] || 0;
            if (v === 0) return null;
            return (
              <div key={ccy} style={{ fontSize: 15, fontWeight: 600, color: P.ink }}>
                {CCY_SYMBOL[ccy]}{v.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                <span style={{ fontSize: 11, color: P.muted, marginLeft: 6 }}>{ccy}</span>
              </div>
            );
          })}
          {(!pv.GBP && !pv.EUR && !pv.USD) && (
            <div style={{ fontSize: 13, color: P.muted }}>No estimated values yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────
function FilterBar({ q, setQ, status, setStatus, sector, setSector,
                     priority, setPriority, dateFrom, setDateFrom,
                     dateTo, setDateTo, onClear }) {
  return (
    <div style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 10,
                  padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) repeat(4, minmax(120px, 1fr)) auto',
                    gap: 10, alignItems: 'center' }} className="leads-filter-grid">
        <div style={{ position: 'relative' }}>
          <Search size={14} color={P.muted}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}/>
          <input
            data-testid="leads-search-input"
            placeholder="Search name, company, email…"
            value={q} onChange={e => setQ(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 32 }}
          />
        </div>
        <select data-testid="leads-status-filter" value={status} onChange={e => setStatus(e.target.value)}
                style={inputStyle}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select data-testid="leads-sector-filter" value={sector} onChange={e => setSector(e.target.value)}
                style={inputStyle}>
          <option value="">All sectors</option>
          {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="leads-priority-filter" value={priority} onChange={e => setPriority(e.target.value)}
                style={inputStyle}>
          <option value="">All priorities</option>
          {Object.entries(PRIORITY_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input data-testid="leads-date-from" type="date" value={dateFrom}
               onChange={e => setDateFrom(e.target.value)} style={inputStyle}/>
        <button data-testid="leads-clear-filters" onClick={onClear} style={btnGhost}>Clear</button>
      </div>
    </div>
  );
}

// ─── Table ───────────────────────────────────────────────────────────
function LeadsTable({ items, loading, onRowClick, onStatusChange }) {
  return (
    <div style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}
             data-testid="leads-table">
        <thead>
          <tr style={{ background: P.borderSoft, textAlign: 'left', color: P.muted,
                       fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            <Th>Received</Th>
            <Th>Contact</Th>
            <Th>Company</Th>
            <Th>Sector</Th>
            <Th>Current system</Th>
            <Th>Priority</Th>
            <Th>Status</Th>
            <Th>Next action</Th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={8} style={{ padding: '32px 16px', textAlign: 'center', color: P.muted }}>
              Loading leads…
            </td></tr>
          )}
          {!loading && items.length === 0 && (
            <tr><td colSpan={8} style={{ padding: '48px 16px', textAlign: 'center', color: P.muted }}>
              <Inbox size={28} style={{ opacity: 0.4, display: 'block', margin: '0 auto 10px' }}/>
              No leads match these filters yet.
            </td></tr>
          )}
          {!loading && items.map(lead => (
            <tr key={lead.id}
                data-testid={`leads-row-${lead.id}`}
                onClick={() => onRowClick(lead)}
                style={{ borderTop: `1px solid ${P.borderSoft}`, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = P.borderSoft + '80'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <Td>
                <div style={{ color: P.ink }}>{fmtDate(lead.received_at)}</div>
                <div style={{ color: P.muted, fontSize: 11 }}>{fmtTime(lead.received_at)}</div>
              </Td>
              <Td>
                <div style={{ color: P.ink, fontWeight: 600 }}>{lead.name || '—'}</div>
                <div style={{ color: P.muted, fontSize: 11 }}>{lead.email || '—'}</div>
              </Td>
              <Td>
                <div style={{ color: P.ink }}>{lead.company || '—'}</div>
                {lead.phone && (
                  <div style={{ color: P.muted, fontSize: 11 }}>{lead.phone}</div>
                )}
              </Td>
              <Td>{lead.sector || <span style={{ color: P.muted }}>—</span>}</Td>
              <Td style={{ maxWidth: 200 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {lead.current_system_display || lead.current_system || <span style={{ color: P.muted }}>—</span>}
                </div>
              </Td>
              <Td>
                <PriorityDot value={lead.priority || 'normal'}/>
              </Td>
              <Td onClick={e => e.stopPropagation()}>
                <StatusSelect
                  value={lead.status || 'new'}
                  onChange={s => onStatusChange(lead.id, s)}
                />
              </Td>
              <Td>
                {lead.next_action_at ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: P.ink }}>
                    <Clock size={12} color={P.slate}/>
                    <span style={{ fontSize: 12 }}>{fmtDate(lead.next_action_at)}</span>
                  </div>
                ) : <span style={{ color: P.muted }}>—</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusSelect({ value, onChange }) {
  const meta = STATUS_META[value] || STATUS_META.new;
  return (
    <select
      data-testid="lead-status-select"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: meta.bg, color: meta.fg, border: `1px solid ${meta.fg}33`,
        fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
        padding: '4px 8px', borderRadius: 6, cursor: 'pointer', outline: 'none',
      }}>
      {Object.entries(STATUS_META).map(([k, m]) => (
        <option key={k} value={k}>{m.label}</option>
      ))}
    </select>
  );
}

function PriorityDot({ value }) {
  const m = PRIORITY_META[value] || PRIORITY_META.normal;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.fg }}/>
      <span style={{ color: P.ink, fontSize: 12 }}>{m.label}</span>
    </div>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────
function Pagination({ page, totalPages, total, pageSize, onPage }) {
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 4px', color: P.slate, fontSize: 13 }}>
      <div>Showing <b>{startIdx}</b>–<b>{endIdx}</b> of <b>{total}</b></div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ ...btnGhost, opacity: page <= 1 ? 0.4 : 1 }}
                data-testid="leads-prev-page">Previous</button>
        <span style={{ padding: '6px 12px', fontVariantNumeric: 'tabular-nums' }}>
          {page} / {totalPages}
        </span>
        <button onClick={() => onPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ ...btnGhost, opacity: page >= totalPages ? 0.4 : 1 }}
                data-testid="leads-next-page">Next</button>
      </div>
    </div>
  );
}

// ─── Drawer ──────────────────────────────────────────────────────────
function LeadDrawer({ lead, onClose, onSave, onAddNote, onArchive }) {
  const [form, setForm] = useState({
    status:        lead.status || 'new',
    priority:      lead.priority || 'normal',
    estimated_value: lead.estimated_value ?? '',
    currency:      lead.currency || 'GBP',
    assigned_to:   lead.assigned_to || '',
    next_action_at: lead.next_action_at || '',
    next_action_note: lead.next_action_note || '',
    tags:          (lead.tags || []).join(', '),
    lost_reason:   lead.lost_reason || '',
  });
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    // Reset form when a different lead is loaded
    setForm({
      status: lead.status || 'new',
      priority: lead.priority || 'normal',
      estimated_value: lead.estimated_value ?? '',
      currency: lead.currency || 'GBP',
      assigned_to: lead.assigned_to || '',
      next_action_at: lead.next_action_at || '',
      next_action_note: lead.next_action_note || '',
      tags: (lead.tags || []).join(', '),
      lost_reason: lead.lost_reason || '',
    });
  }, [lead.id]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        status: form.status,
        priority: form.priority,
        currency: form.currency,
        assigned_to: form.assigned_to,
        next_action_at: form.next_action_at || '',
        next_action_note: form.next_action_note,
        lost_reason: form.lost_reason,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      const raw = String(form.estimated_value).trim();
      patch.estimated_value = raw === '' ? null : Number(raw);
      await onSave(lead.id, patch);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    await onAddNote(lead.id, noteText.trim());
    setNoteText('');
  };

  return (
    <div data-testid="lead-drawer" style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex' }}>
      <div onClick={onClose} style={{ flex: 1, background: 'rgba(15, 23, 42, 0.35)' }}/>
      <aside style={{
        width: 'min(560px, 96vw)', height: '100%', background: P.surface,
        boxShadow: '-8px 0 32px rgba(15,23,42,0.16)', overflowY: 'auto',
      }}>
        <header style={{ position: 'sticky', top: 0, background: P.surface,
                         borderBottom: `1px solid ${P.border}`, padding: '18px 22px',
                         display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                         gap: 12, zIndex: 2 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                          fontWeight: 700, color: P.muted, marginBottom: 4 }}>Lead</div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: P.ink }}>
              {lead.name || 'Unnamed'} — {lead.company || '—'}
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: P.muted }}>
              Received {fmtDateTime(lead.received_at)}
            </div>
          </div>
          <button onClick={onClose} data-testid="lead-drawer-close" style={btnIcon}>
            <X size={16}/>
          </button>
        </header>

        <div style={{ padding: '18px 22px' }}>
          {/* Contact block */}
          <Section title="Contact">
            <KV label="Email"><a href={`mailto:${lead.email}`} style={{ color: P.accent, textDecoration: 'none' }}>{lead.email || '—'}</a></KV>
            <KV label="Phone">{lead.phone ? (
              <a href={`tel:${lead.phone}`} style={{ color: P.accent, textDecoration: 'none' }}>{lead.phone}</a>
            ) : '—'}</KV>
            <KV label="Sector">{lead.sector || '—'}</KV>
            <KV label="Current system">{lead.current_system_display || lead.current_system || '—'}</KV>
            {lead.source_path && (
              <KV label="Source page">
                <a href={lead.source_path} target="_blank" rel="noreferrer"
                   style={{ color: P.accent, textDecoration: 'none' }}>
                  {lead.source_path} <ExternalLink size={11} style={{ verticalAlign: 'text-top' }}/>
                </a>
              </KV>
            )}
          </Section>

          {/* Message */}
          <Section title="Their message" icon={<MessageCircle size={13} color={P.muted}/>}>
            <div style={{
              padding: 14, background: P.borderSoft, borderRadius: 8,
              fontSize: 13.5, lineHeight: 1.6, color: P.ink, whiteSpace: 'pre-wrap',
            }} data-testid="lead-message">
              {lead.message || <span style={{ color: P.muted }}>(no message provided)</span>}
            </div>
          </Section>

          {/* Pipeline fields */}
          <Section title="Pipeline">
            <Row>
              <Field label="Status">
                <select data-testid="drawer-status" value={form.status}
                        onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                        style={inputStyle}>
                  {Object.entries(STATUS_META).map(([k, m]) =>
                    <option key={k} value={k}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <select data-testid="drawer-priority" value={form.priority}
                        onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                        style={inputStyle}>
                  {Object.entries(PRIORITY_META).map(([k, m]) =>
                    <option key={k} value={k}>{m.label}</option>)}
                </select>
              </Field>
            </Row>
            <Row>
              <Field label="Estimated deal value">
                <div style={{ display: 'flex', gap: 6 }}>
                  <select data-testid="drawer-currency" value={form.currency}
                          onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                          style={{ ...inputStyle, width: 90 }}>
                    <option value="GBP">£ GBP</option>
                    <option value="EUR">€ EUR</option>
                    <option value="USD">$ USD</option>
                  </select>
                  <input data-testid="drawer-value" type="number" min="0"
                         placeholder="0"
                         value={form.estimated_value}
                         onChange={e => setForm(f => ({ ...f, estimated_value: e.target.value }))}
                         style={{ ...inputStyle, flex: 1 }}/>
                </div>
              </Field>
              <Field label="Assigned to">
                <input data-testid="drawer-assigned" placeholder="admin@…"
                       value={form.assigned_to}
                       onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                       style={inputStyle}/>
              </Field>
            </Row>
            <Row>
              <Field label="Next action date">
                <input data-testid="drawer-next-action-at" type="date"
                       value={form.next_action_at?.slice(0, 10) || ''}
                       onChange={e => setForm(f => ({ ...f, next_action_at: e.target.value }))}
                       style={inputStyle}/>
              </Field>
              <Field label="Next action">
                <input data-testid="drawer-next-action-note"
                       placeholder="e.g. Call back Tuesday"
                       value={form.next_action_note}
                       onChange={e => setForm(f => ({ ...f, next_action_note: e.target.value }))}
                       style={inputStyle}/>
              </Field>
            </Row>
            <Field label={<><Tag size={11} style={{ verticalAlign: 'middle', marginRight: 4 }}/>Tags (comma-separated)</>}>
              <input data-testid="drawer-tags" value={form.tags}
                     placeholder="cheese, referral, warm"
                     onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                     style={inputStyle}/>
            </Field>
            {form.status === 'lost' && (
              <Field label="Lost reason">
                <input data-testid="drawer-lost-reason" value={form.lost_reason}
                       placeholder="Went with competitor / no budget / …"
                       onChange={e => setForm(f => ({ ...f, lost_reason: e.target.value }))}
                       style={inputStyle}/>
              </Field>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
              <button data-testid="drawer-save-btn" onClick={save} disabled={saving} style={btnPrimary}>
                <Save size={14}/> {saving ? 'Saving…' : 'Save changes'}
              </button>
              {savedFlash && (
                <span style={{ color: P.success, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle2 size={13}/> Saved
                </span>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <button data-testid="drawer-archive-btn" onClick={() => onArchive(lead.id)}
                        style={{ ...btnGhost, color: P.danger }}>
                  <Trash2 size={13}/> Archive
                </button>
              </div>
            </div>
          </Section>

          {/* Notes */}
          <Section title={`Notes & activity (${(lead.notes || []).length})`}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <textarea data-testid="drawer-note-input" value={noteText}
                        onChange={e => setNoteText(e.target.value)}
                        placeholder="Add a note about this conversation…"
                        rows={2}
                        style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}/>
            </div>
            <button data-testid="drawer-note-submit" onClick={submitNote}
                    disabled={!noteText.trim()}
                    style={{ ...btnGhost, opacity: !noteText.trim() ? 0.4 : 1 }}>
              Add note
            </button>

            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
                 data-testid="drawer-notes-list">
              {[...(lead.notes || [])].reverse().map((n, i) => (
                <div key={i} style={{
                  background: P.borderSoft, borderRadius: 8, padding: '10px 12px',
                }}>
                  <div style={{ fontSize: 11, color: P.muted, marginBottom: 4 }}>
                    {fmtDateTime(n.at)}{n.author ? ` · ${n.author}` : ''}
                  </div>
                  <div style={{ fontSize: 13, color: P.ink, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {n.text}
                  </div>
                </div>
              ))}
              {(lead.notes || []).length === 0 && (
                <div style={{ color: P.muted, fontSize: 12, fontStyle: 'italic' }}>
                  No notes yet — jot the outcome of your last call here.
                </div>
              )}
            </div>
          </Section>

          {/* History */}
          {(lead.history || []).length > 0 && (
            <Section title="Change history">
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12, color: P.slate }}>
                {[...lead.history].reverse().map((h, i) => (
                  <li key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${P.borderSoft}` }}>
                    <span style={{ color: P.muted }}>{fmtDateTime(h.at)} — </span>
                    <b>{h.field}</b> changed from <code style={code}>{fmtVal(h.from)}</code>
                    {' '}to <code style={code}>{fmtVal(h.to)}</code>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </aside>
    </div>
  );
}

// ─── Presentational helpers ─────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <section style={{ marginBottom: 22 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
      {icon}
      <h3 style={{ margin: 0, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                   fontWeight: 700, color: P.muted }}>{title}</h3>
    </div>
    {children}
  </section>
);
const KV = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12, padding: '6px 0',
                fontSize: 13, borderBottom: `1px solid ${P.borderSoft}` }}>
    <div style={{ color: P.muted, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontWeight: 700, alignSelf: 'center' }}>{label}</div>
    <div style={{ color: P.ink }}>{children}</div>
  </div>
);
const Row = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>{children}</div>
);
const Field = ({ label, children }) => (
  <label style={{ display: 'block' }}>
    <span style={{ display: 'block', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                   fontWeight: 700, color: P.muted, marginBottom: 4 }}>{label}</span>
    {children}
  </label>
);
const Th = ({ children }) => (
  <th style={{ padding: '10px 14px', fontWeight: 700 }}>{children}</th>
);
const Td = ({ children, ...rest }) => (
  <td style={{ padding: '12px 14px', verticalAlign: 'top' }} {...rest}>{children}</td>
);

// ─── Styles ─────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${P.border}`,
  borderRadius: 6, fontSize: 13, color: P.ink, background: P.surface,
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: P.primary, color: '#fff', border: 'none', padding: '8px 14px',
  fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
};
const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: P.surface, color: P.slate, border: `1px solid ${P.border}`,
  padding: '8px 12px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
};
const btnIcon = {
  background: P.borderSoft, border: 'none', width: 32, height: 32, borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: P.slate,
};
const code = {
  background: P.primarySoft, color: P.primary, padding: '1px 5px', borderRadius: 3, fontSize: 11,
};

// ─── Formatters ─────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}
function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined,
      { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined,
      { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
function fmtVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ') || '—';
  return String(v);
}
